import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, TextField, Card, CardContent, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Box, Select, MenuItem, FormControl,
  InputLabel, Grid, CircularProgress, Alert, Chip, Button,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton
} from '@mui/material';
import { Search, FilterList, Edit, Delete, Warning, Public, Lock } from '@mui/icons-material';
import { getAlumniList, getFilterOptions } from '../services/api';
import { useAuth } from '../App';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = API_BASE_URL;

// Helper function to normalize degree names for emphasis lookup
const normalizeDegreeKey = (degreeName) => {
  return degreeName
    .replace(/\./g, '')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const BrowsePage = () => {
  const { user } = useAuth();
  const [alumni, setAlumni] = useState([]);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Edit/Delete state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [deletionReason, setDeletionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Form options for edit modal
  const [formOptions, setFormOptions] = useState({
    schools: {},
    courseLevels: [],
    honors: [],
    countries: []
  });

  // Emphasis state for edit modal
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedDegree, setSelectedDegree] = useState('');
  const [availableEmphases, setAvailableEmphases] = useState([]);
  const [selectedEmphasis, setSelectedEmphasis] = useState('');
  const [otherEmphasisText, setOtherEmphasisText] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('All');
  const [honorsFilter, setHonorsFilter] = useState('All');
  const [countryFilter, setCountryFilter] = useState('All');
  const [gradYearFilter, setGradYearFilter] = useState('All');

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    genders: [],
    honors: [],
    countries: [],
    gradYears: []
  });

  const GENDERS = ["Male", "Female"];

  useEffect(() => {
    fetchFilterOptions();
    if (isAdmin) {
      fetchFormOptions();
    }
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const data = await getFilterOptions();
      setFilterOptions(data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/form-options`);

      // The API returns schools/degrees/emphases nested per era under
      // programsByEra (same structure AlumniRegistrationPage navigates).
      // Flatten every era into one merged schools object — same approach
      // AdminAddRequestsPage already uses successfully for its edit modal.
      const allSchools = {};

      if (response.data.programsByEra) {
        Object.keys(response.data.programsByEra).forEach((era) => {
          const eraData = response.data.programsByEra[era];
          const units = eraData.faculties || eraData.schools || {};

          Object.keys(units).forEach((unitName) => {
            const unit = units[unitName];

            if (!allSchools[unitName]) {
              allSchools[unitName] = { degrees: [], degreeLabels: {}, emphases: {} };
            }

            if (unit.degrees) {
              Object.keys(unit.degrees).forEach((degreeKey) => {
                const degreeData = unit.degrees[degreeKey];
                const degreeLabel = degreeData.label || degreeKey;

                if (!allSchools[unitName].degrees.includes(degreeLabel)) {
                  allSchools[unitName].degrees.push(degreeLabel);
                }
                allSchools[unitName].degreeLabels[degreeLabel] = degreeKey;

                if (degreeData.emphases) {
                  if (!allSchools[unitName].emphases[degreeLabel]) {
                    allSchools[unitName].emphases[degreeLabel] = [];
                  }
                  Object.keys(degreeData.emphases).forEach((emphasisKey) => {
                    if (!allSchools[unitName].emphases[degreeLabel].includes(emphasisKey)) {
                      allSchools[unitName].emphases[degreeLabel].push(emphasisKey);
                    }
                  });
                }
              });
            }
          });
        });
      }

      setFormOptions({
        schools: allSchools,
        courseLevels: response.data.courseLevels || ['Certificate', 'Diploma', 'Associate Degree', 'Bachelor Degree', 'Undergraduate', 'Graduate'],
        honors: response.data.honors || ['', 'Cum Laude', 'Magna Cum Laude', 'Summa Cum Laude'],
        countries: response.data.countries || ['Trinidad and Tobago', 'United States', 'Canada', 'United Kingdom']
      });
    } catch (err) {
      console.error('Error fetching form options:', err);
    }
  };

  const handleSearch = async () => {
    const hasFilter =
      search.trim() !== '' ||
      genderFilter !== 'All' ||
      honorsFilter !== 'All' ||
      countryFilter !== 'All' ||
      gradYearFilter !== 'All';

    if (!hasFilter) {
      setError('Please enter a search term or select at least one filter to narrow results.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setHasSearched(true);

    try {
      const token = Cookies.get('idToken');

      // Use the new privacy-aware browse endpoint
      const response = await axios.get(`${API_BASE}/alumni/browse`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: search.trim(),
          gender: genderFilter !== 'All' ? genderFilter : '',
          honors: honorsFilter !== 'All' ? honorsFilter : '',
          country: countryFilter !== 'All' ? countryFilter : '',
          gradYear: gradYearFilter !== 'All' ? gradYearFilter : ''
        }
      });

      setAlumni(response.data.alumni || []);

      if (response.data.alumni && response.data.alumni.length === 0) {
        setError('No alumni found matching your search criteria. Try adjusting your filters.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load alumni data');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setGenderFilter('All');
    setHonorsFilter('All');
    setCountryFilter('All');
    setGradYearFilter('All');
    setAlumni([]);
    setSelectedAlumni(null);
    setSelectedIndex(null);
    setHasSearched(false);
    setError('');
    setSuccess('');
  };

  const handleRowClick = (alumnus, index) => {
    setSelectedAlumni(alumnus);
    setSelectedIndex(index);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // ========== EDIT FUNCTIONALITY (Admin Only) ==========
  const handleOpenEdit = () => {
    if (!selectedAlumni || !isAdmin) return;

    // Keep original identifiers so the backend can locate the row even if the
    // admin edits the email or name during this session.
    setEditedData({
      ...selectedAlumni,
      _original_email: selectedAlumni['Email Contact'] || '',
      _original_alumni_id: selectedAlumni.AlumniID || ''
    });

    // Parse degree and emphasis
    const school = selectedAlumni['School'] || '';
    const degreeWithEmphasis = selectedAlumni['Degree Obtained'] || '';

    let degree = degreeWithEmphasis;
    let emphasis = '';

    if (degreeWithEmphasis.includes(' - ')) {
      const parts = degreeWithEmphasis.split(' - ');
      degree = parts[0];
      emphasis = parts[1];
    }

    setSelectedSchool(school);
    setSelectedDegree(degree);
    setSelectedEmphasis(emphasis);

    // Load emphases for this degree — emphases are stored keyed by the raw
    // degree label (see fetchFormOptions), not a normalized key
    if (school && degree && formOptions.schools?.[school]?.emphases) {
      const emphases = formOptions.schools[school].emphases[degree] || [];
      setAvailableEmphases(emphases);

      if (emphasis && !emphases.includes(emphasis) && emphases.includes('Other')) {
        setSelectedEmphasis('Other');
        setOtherEmphasisText(emphasis);
      } else {
        setOtherEmphasisText('');
      }
    } else {
      setAvailableEmphases([]);
      setOtherEmphasisText('');
    }

    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditedData({});
    setSelectedSchool('');
    setSelectedDegree('');
    setSelectedEmphasis('');
    setAvailableEmphases([]);
    setOtherEmphasisText('');
  };

  const handleEditChange = (field) => (event) => {
    setEditedData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSchoolChange = (school) => {
    setSelectedSchool(school);
    setSelectedDegree('');
    setSelectedEmphasis('');
    setAvailableEmphases([]);
    setOtherEmphasisText('');
    setEditedData({
      ...editedData,
      School: school,
      'Degree Obtained': ''
    });
  };

  const handleDegreeChange = (degree) => {
    setSelectedDegree(degree);
    setSelectedEmphasis('');
    setOtherEmphasisText('');

    // Emphases are stored keyed by the raw degree label (see fetchFormOptions)
    const emphases = formOptions.schools?.[selectedSchool]?.emphases?.[degree] || [];
    setAvailableEmphases(emphases);

    setEditedData({
      ...editedData,
      'Degree Obtained': degree
    });
  };

  const handleEmphasisChange = (emphasis) => {
    setSelectedEmphasis(emphasis);

    if (emphasis === 'Other') {
      setOtherEmphasisText('');
      setEditedData({
        ...editedData,
        'Degree Obtained': selectedDegree
      });
    } else if (emphasis) {
      setOtherEmphasisText('');
      setEditedData({
        ...editedData,
        'Degree Obtained': `${selectedDegree} - ${emphasis}`
      });
    } else {
      setOtherEmphasisText('');
      setEditedData({
        ...editedData,
        'Degree Obtained': selectedDegree
      });
    }
  };

  const handleOtherEmphasisChange = (text) => {
    setOtherEmphasisText(text);
    if (text.trim()) {
      setEditedData({
        ...editedData,
        'Degree Obtained': `${selectedDegree} - ${text}`
      });
    } else {
      setEditedData({
        ...editedData,
        'Degree Obtained': selectedDegree
      });
    }
  };

  const handleSaveEdit = async () => {
    if (selectedEmphasis === 'Other' && !otherEmphasisText.trim()) {
      setError('Please specify the emphasis when selecting "Other"');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const token = Cookies.get('idToken');

      // Send the full edited data - backend will use composite key to find the record
      await axios.put(
        `${API_BASE}/admin/alumni/update`,
        editedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Alumni record updated successfully!');
      handleCloseEdit();

      // Refresh search results
      await handleSearch();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating alumni:', err);
      setError(err.response?.data?.error || 'Failed to update alumni record');
    } finally {
      setProcessing(false);
    }
  };

  // ========== DELETE FUNCTIONALITY (Admin Only) ==========
  const handleOpenDelete = () => {
    if (!selectedAlumni || !isAdmin) return;
    setDeleteModalOpen(true);
    setDeletionReason('');
  };

  const handleCloseDelete = () => {
    setDeleteModalOpen(false);
    setDeletionReason('');
  };

  const handleConfirmDelete = async () => {
    setProcessing(true);
    setError('');

    try {
      const token = Cookies.get('idToken');

      // Send the full alumni data - backend will use composite key to find the record
      await axios.delete(
        `${API_BASE}/admin/alumni/delete`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            alumni_data: selectedAlumni,
            reason: deletionReason || 'No reason provided'
          }
        }
      );

      setSuccess('Alumni record deleted successfully (moved to DeletedRecords)');
      handleCloseDelete();
      setSelectedAlumni(null);
      setSelectedIndex(null);

      // Refresh search results
      await handleSearch();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting alumni:', err);
      setError(err.response?.data?.error || 'Failed to delete alumni record');
    } finally {
      setProcessing(false);
    }
  };

  const isAdmin = user?.role === 'Admin';
  const isViewOnly = user?.role === 'View Only';
  const isBusiness = user?.role === 'Business';

  const degreeOptions = selectedSchool && formOptions.schools?.[selectedSchool]
    ? formOptions.schools[selectedSchool].degrees || []
    : [];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Alumni Directory
      </Typography>

      {/* Privacy Notice for Business/View Only Users */}
      {(isBusiness || isViewOnly) && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<Public />}>
          <Typography variant="body2">
            <strong>Privacy Notice:</strong> You are viewing alumni who have opted to share their profiles with employers.
            Only limited information is displayed to protect alumni privacy.
          </Typography>
        </Alert>
      )}

      {/* General Info Alert */}
      {isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Admin View:</strong> You can see all alumni and their privacy status.
            Alumni with a <Lock fontSize="small" sx={{ verticalAlign: 'middle' }} /> icon have chosen to keep their profile private.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Search and Filters */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList /> Search & Filter Alumni
              </Typography>

              <TextField
                fullWidth
                placeholder="Search by name (e.g., John Doe)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
                helperText="Press Enter or click Search"
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Hide gender filter for Business users */}
                {!isBusiness && (
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={genderFilter}
                        onChange={(e) => setGenderFilter(e.target.value)}
                        label="Gender"
                      >
                        <MenuItem value="All">All</MenuItem>
                        {filterOptions.genders.map((gender) => (
                          <MenuItem key={gender} value={gender}>
                            {gender}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Honors</InputLabel>
                    <Select
                      value={honorsFilter}
                      onChange={(e) => setHonorsFilter(e.target.value)}
                      label="Honors"
                    >
                      <MenuItem value="All">All</MenuItem>
                      {filterOptions.honors.map((honor) => (
                        <MenuItem key={honor} value={honor}>
                          {honor}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                      label="Country"
                    >
                      <MenuItem value="All">All</MenuItem>
                      {filterOptions.countries.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Grad Year</InputLabel>
                    <Select
                      value={gradYearFilter}
                      onChange={(e) => setGradYearFilter(e.target.value)}
                      label="Grad Year"
                    >
                      <MenuItem value="All">All</MenuItem>
                      {filterOptions.gradYears.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? 'Searching...' : 'Search Alumni'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={loading}
                >
                  Clear
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Results Table */}
          {hasSearched && (
            <Card>
              <CardContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Search Results
                      </Typography>
                      <Chip
                        label={`${alumni.length} alumni found`}
                        color={alumni.length > 0 ? 'primary' : 'default'}
                      />
                    </Box>

                    <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 500 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {isAdmin && <TableCell width={50}></TableCell>}
                            <TableCell><strong>First Name</strong></TableCell>
                            <TableCell><strong>Last Name</strong></TableCell>
                            <TableCell><strong>Graduation Year</strong></TableCell>
                            <TableCell><strong>Degree</strong></TableCell>
                            <TableCell><strong>Country</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {alumni.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={isAdmin ? 6 : 5} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                  No alumni found. Try different search criteria.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            alumni.map((alumnus, index) => (
                              <TableRow
                                key={index}
                                hover
                                onClick={() => handleRowClick(alumnus, index)}
                                sx={{
                                  cursor: 'pointer',
                                  backgroundColor: selectedIndex === index ? 'action.selected' : 'inherit'
                                }}
                              >
                                {isAdmin && (
                                  <TableCell>
                                    {alumnus['Share With Employers'] === 'No' && (
                                      <Lock fontSize="small" color="action" />
                                    )}
                                  </TableCell>
                                )}
                                <TableCell>{alumnus.First_Name}</TableCell>
                                <TableCell>{alumnus.Last_Name}</TableCell>
                                <TableCell>{alumnus.graduation}</TableCell>
                                <TableCell>{alumnus['Degree Obtained']}</TableCell>
                                <TableCell>{alumnus.Country}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {!hasSearched && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Search sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Ready to Search
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter a name or select filters above to begin searching the alumni directory
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right: Selected Alumni Details */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Alumni Details
              </Typography>

              {selectedAlumni ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" color="primary">
                      {selectedAlumni.First_Name} {selectedAlumni.Last_Name}
                    </Typography>
                    {isAdmin && selectedAlumni['Share With Employers'] === 'No' && (
                      <Chip
                        icon={<Lock fontSize="small" />}
                        label="Private"
                        size="small"
                        color="default"
                      />
                    )}
                    {isAdmin && selectedAlumni['Share With Employers'] === 'Yes' && (
                      <Chip
                        icon={<Public fontSize="small" />}
                        label="Public"
                        size="small"
                        color="success"
                      />
                    )}
                  </Box>

                  {/* Admin Action Buttons */}
                  {isAdmin && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={handleOpenEdit}
                        fullWidth
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleOpenDelete}
                        fullWidth
                      >
                        Delete
                      </Button>
                    </Box>
                  )}

                  {/* Fields visible to Business users */}
                  <DetailItem label="Graduation Year" value={selectedAlumni.graduation} />
                  <DetailItem label="Degree" value={selectedAlumni['Degree Obtained']} />
                  <DetailItem label="School" value={selectedAlumni.School} />
                  <DetailItem label="Country" value={selectedAlumni.Country} />

                  {/* Additional fields for View Only and Admin */}
                  {(isAdmin || isViewOnly) && (
                    <>
                      <DetailItem label="Email" value={selectedAlumni['Email Contact']} />
                      <DetailItem label="Contact Number" value={selectedAlumni['Contact Number']} />
                      <DetailItem label="Job Title" value={selectedAlumni['Current Job Title']} />
                    </>
                  )}

                  {selectedAlumni.Honors && (
                    <Box sx={{ mt: 1 }}>
                      <Chip label={selectedAlumni.Honors} color="secondary" size="small" />
                    </Box>
                  )}

                  {/* Admin-only fields */}
                  {isAdmin && (
                    <>
                      <DetailItem label="USC Email" value={selectedAlumni['USC Email']} />
                      <DetailItem label="Alt Phone" value={selectedAlumni['Alternative Phone Number']} />
                      <DetailItem label="Address" value={selectedAlumni.Address} />
                      <DetailItem label="Gender" value={selectedAlumni.Gender} />
                      <DetailItem label="Student ID" value={selectedAlumni['Student Id']} />
                      <DetailItem label="Alumni ID" value={selectedAlumni.AlumniID} />
                      <DetailItem label="Department" value={selectedAlumni.Department} />
                      <DetailItem label="Other Degrees" value={selectedAlumni['Other Degree Attained']} />
                    </>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasSearched
                      ? 'Click on an alumni from the search results to view their details'
                      : 'Search for alumni to view their details'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Modal (Admin Only) */}
      {isAdmin && (
        <>
          <Dialog open={editModalOpen} onClose={handleCloseEdit} maxWidth="md" fullWidth>
            <DialogTitle>Edit Alumni Record</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* Personal Information */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      Personal Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={editedData.First_Name || ''}
                      onChange={handleEditChange('First_Name')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={editedData.Last_Name || ''}
                      onChange={handleEditChange('Last_Name')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Gender"
                      value={editedData.Gender || ''}
                      onChange={handleEditChange('Gender')}
                    >
                      {GENDERS.map((gender) => (
                        <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Student ID"
                      value={editedData['Student Id'] || ''}
                      onChange={handleEditChange('Student Id')}
                    />
                  </Grid>

                  {/* Academic Information */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      Academic Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="School"
                      value={selectedSchool}
                      onChange={(e) => handleSchoolChange(e.target.value)}
                    >
                      {Object.keys(formOptions.schools || {}).map((school) => (
                        <MenuItem key={school} value={school}>{school}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Degree"
                      value={selectedDegree}
                      onChange={(e) => handleDegreeChange(e.target.value)}
                      disabled={!selectedSchool}
                    >
                      {degreeOptions.map((degree) => (
                        <MenuItem key={degree} value={degree}>{degree}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {availableEmphases.length > 0 && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Emphasis/Concentration"
                        value={selectedEmphasis}
                        onChange={(e) => handleEmphasisChange(e.target.value)}
                      >
                        <MenuItem value="">None</MenuItem>
                        {availableEmphases.map((emphasis) => (
                          <MenuItem key={emphasis} value={emphasis}>{emphasis}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {selectedEmphasis === 'Other' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Specify Other Emphasis"
                        value={otherEmphasisText}
                        onChange={(e) => handleOtherEmphasisChange(e.target.value)}
                        placeholder="Enter custom emphasis"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Course Level"
                      value={editedData['Course Level'] || ''}
                      onChange={handleEditChange('Course Level')}
                    >
                      {formOptions.courseLevels.map((level) => (
                        <MenuItem key={level} value={level}>{level}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Graduation Year"
                      type="number"
                      value={editedData.graduation || ''}
                      onChange={handleEditChange('graduation')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Honors"
                      value={editedData.Honors || ''}
                      onChange={handleEditChange('Honors')}
                    >
                      {formOptions.honors.map((honor) => (
                        <MenuItem key={honor} value={honor}>{honor || 'None'}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Contact Information */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      Contact Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Contact"
                      type="email"
                      value={editedData['Email Contact'] || ''}
                      onChange={handleEditChange('Email Contact')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      value={editedData['Contact Number'] || ''}
                      onChange={handleEditChange('Contact Number')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Country"
                      value={editedData.Country || ''}
                      onChange={handleEditChange('Country')}
                    >
                      {formOptions.countries.map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={editedData.Address || ''}
                      onChange={handleEditChange('Address')}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={processing}
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteModalOpen} onClose={handleCloseDelete} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="error" />
                Delete Alumni Record
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will move the record to <strong>DeletedRecords</strong> sheet. The data will not be permanently deleted.
              </Alert>

              <Typography variant="body2" gutterBottom>
                You are about to delete: <strong>{selectedAlumni?.First_Name} {selectedAlumni?.Last_Name}</strong>
              </Typography>

              <TextField
                fullWidth
                label="Reason for Deletion (Optional)"
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                multiline
                rows={3}
                sx={{ mt: 2 }}
                placeholder="e.g., Duplicate record, Invalid data, Requested by alumni..."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDelete}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDelete}
                disabled={processing}
                startIcon={<Delete />}
              >
                {processing ? 'Deleting...' : 'Confirm Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Container>
  );
};

const DetailItem = ({ label, value }) => {
  if (!value) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">
        {value}
      </Typography>
    </Box>
  );
};

export default BrowsePage;